import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const message = formData.get("message") as string;
    if (message && message.length > 500) {
      return;
    }

    setSubmitting(true);
    const params = new URLSearchParams();
    params.append("form-name", "contact");
    formData.forEach((value, key) => {
      params.append(key, value as string);
    });

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/contact-hope2026.jpg"
              alt="Welcoming space"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#418b86]/50 via-[#418b86]/40 to-[#418b86]/55"></div>
          </div>
          <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-10 text-center">
            <p className="text-[#7ec8e3] text-xs font-medium tracking-widest uppercase mb-4">Reach Out</p>
            <h1 className="text-4xl md:text-5xl font-light text-white leading-tight mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              Taking the first step<br />
              <span className="italic font-semibold">takes courage.</span>
            </h1>
            <p className="text-white/75 text-base leading-relaxed max-w-xl mx-auto">
              You don't need to have the right words. Just reach out — we'll take it from there, together.
            </p>
          </div>
        </section>

        {/* Form + Info */}
        <section className="bg-[#f0f9f9] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
              {/* Left: Info */}
              <div>
                <p className="text-[#9a9a9a] text-xs font-medium tracking-widest uppercase mb-4">Get in Touch</p>
                <h2 className="text-3xl md:text-4xl font-light text-[#2d2d2d] leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  We'd love to<br />
                  <span className="italic font-semibold">hear from you.</span>
                </h2>
                <p className="text-[#6b6b6b] text-sm leading-relaxed mb-10">
                  Whether you have a question or just want to connect, we're here. Every inquiry is handled with care and complete confidentiality.
                </p>

                {/* Contact Details */}
                <div className="flex flex-col gap-5 mb-10">
                  {[
                    { icon: "ri-mail-line", label: "Email", value: "rholmes@claritypoint-counseling.com", href: "mailto:rholmes@claritypoint-counseling.com" },
                    { icon: "ri-phone-line", label: "Phone", value: "508 566 7333", href: "tel:5085667333" },
                    { icon: "ri-map-pin-line", label: "Location", value: "Johnson County, Iowa area" },
                    { icon: "ri-time-line", label: "Hours", value: "Sun – Fri: 8am – 5pm\nSaturday: Closed" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#e6f6f5] flex-shrink-0">
                        <i className={`${item.icon} text-[#4db8b1] text-base`}></i>
                      </div>
                      <div>
                        <p className="text-[#9a9a9a] text-xs font-medium uppercase tracking-wider mb-0.5">{item.label}</p>
                        {item.href ? (
                          <a href={item.href} className="text-[#2d2d2d] text-sm whitespace-pre-line hover:text-[#418b86] transition-colors cursor-pointer">
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-[#2d2d2d] text-sm whitespace-pre-line">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reassurance */}
                <div className="bg-white rounded-xl p-6 border border-[#c8e8e6]">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#e6f6f5] flex-shrink-0">
                      <i className="ri-shield-check-line text-[#4db8b1] text-sm"></i>
                    </div>
                    <div>
                      <p className="text-[#2d2d2d] text-sm font-medium mb-1">Your privacy is protected.</p>
                      <p className="text-[#6b6b6b] text-xs leading-relaxed">
                        All communications are completely confidential. Reaching out does not create a therapeutic relationship or any obligation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Form */}
              <div className="bg-white rounded-2xl p-8 md:p-10 border border-[#c8e8e6]">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#e6f6f5] mx-auto mb-5">
                      <i className="ri-check-line text-[#4db8b1] text-2xl"></i>
                    </div>
                    <h3 className="text-[#2d2d2d] text-xl font-semibold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Message Received
                    </h3>
                    <p className="text-[#6b6b6b] text-sm leading-relaxed max-w-xs mx-auto">
                      Thank you for reaching out. We'll be in touch within 1–2 business days. You've taken a brave first step.
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-[#2d2d2d] text-xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Send Us a Message
                    </h3>
                    <p className="text-[#9a9a9a] text-xs mb-7">
                      Have a question or just want to connect? We'll respond within 1–2 business days.
                    </p>

                    <form
                      name="contact"
                      method="POST"
                      data-netlify="true"
                      netlify-honeypot="bot-field"
                      id="contact-form"
                      onSubmit={handleSubmit}
                      className="flex flex-col gap-5"
                    >
                      <input type="hidden" name="form-name" value="contact" />
                      <p className="hidden">
                        <label>
                          Don't fill this out if you're human: <input name="bot-field" />
                        </label>
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[#4a4a4a] text-xs font-medium mb-1.5">First Name *</label>
                          <input
                            type="text"
                            name="first_name"
                            required
                            placeholder="Sarah"
                            className="w-full border border-[#c8e8e6] rounded-lg px-4 py-3 text-sm text-[#2d2d2d] placeholder-[#a0c8c6] focus:outline-none focus:border-[#4db8b1] bg-[#f6fcfc] transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-[#4a4a4a] text-xs font-medium mb-1.5">Last Name *</label>
                          <input
                            type="text"
                            name="last_name"
                            required
                            placeholder="Johnson"
                            className="w-full border border-[#c8e8e6] rounded-lg px-4 py-3 text-sm text-[#2d2d2d] placeholder-[#a0c8c6] focus:outline-none focus:border-[#4db8b1] bg-[#f6fcfc] transition-colors"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[#4a4a4a] text-xs font-medium mb-1.5">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="sarah@example.com"
                          className="w-full border border-[#c8e8e6] rounded-lg px-4 py-3 text-sm text-[#2d2d2d] placeholder-[#a0c8c6] focus:outline-none focus:border-[#4db8b1] bg-[#f6fcfc] transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-[#4a4a4a] text-xs font-medium mb-1.5">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          placeholder="(555) 000-0000"
                          className="w-full border border-[#c8e8e6] rounded-lg px-4 py-3 text-sm text-[#2d2d2d] placeholder-[#a0c8c6] focus:outline-none focus:border-[#4db8b1] bg-[#f6fcfc] transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-[#4a4a4a] text-xs font-medium mb-1.5">Area of Interest</label>
                        <select
                          name="service_interest"
                          className="w-full border border-[#c8e8e6] rounded-lg px-4 py-3 text-sm text-[#2d2d2d] focus:outline-none focus:border-[#4db8b1] bg-[#f6fcfc] transition-colors cursor-pointer"
                        >
                          <option value="">Select a service...</option>
                          <option value="Individual Counseling">Individual Counseling</option>
                          <option value="Anxiety & Stress Management">Anxiety &amp; Stress Management</option>
                          <option value="Life Transitions">Life Transitions</option>
                          <option value="Relationship Support">Relationship Support</option>
                          <option value="Not Sure Yet">Not Sure Yet</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[#4a4a4a] text-xs font-medium mb-1.5">
                          How can we help? <span className="text-[#c0b8b0] font-normal">(optional)</span>
                        </label>
                        <textarea
                          name="message"
                          rows={4}
                          maxLength={500}
                          placeholder="You can share as much or as little as you'd like. There's no pressure to have it all figured out."
                          className="w-full border border-[#c8e8e6] rounded-lg px-4 py-3 text-sm text-[#2d2d2d] placeholder-[#a0c8c6] focus:outline-none focus:border-[#4db8b1] bg-[#f6fcfc] transition-colors resize-none"
                        />
                        <p className="text-[#c0b8b0] text-xs mt-1">Max 500 characters</p>
                      </div>

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#418b86] hover:bg-[#4db8b1] disabled:opacity-60 text-white font-medium py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2"
                      >
                        {submitting ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            Sending...
                          </>
                        ) : (
                          <>
                            Send Message
                            <i className="ri-arrow-right-line"></i>
                          </>
                        )}
                      </button>

                      <p className="text-[#9a9a9a] text-xs text-center leading-relaxed">
                        By submitting, you agree to be contacted about your inquiry. All information is kept strictly confidential.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
